import { PredictionsLocalCache, WSPredictionResponse } from '../types';

const TTL_MS = 2 * 60 * 1000; // 2 minutes

export interface PredictionsState {
  cache: PredictionsLocalCache;
  dismissedPairIds: Set<string>;
}

export const initialState: PredictionsState = {
  cache: new Map(),
  dismissedPairIds: new Set()
};

const pruneExpired = (map: PredictionsLocalCache): PredictionsLocalCache => {
  const cutoff = Date.now() - TTL_MS;
  let changed = false;
  for (const [key, val] of map) {
    if (val.timestamp < cutoff) {
      map.delete(key);
      changed = true;
    }
  }
  return changed
    ? new Map(
        [...map.entries()].sort(([, a], [, b]) => b.takerTna - a.takerTna)
      )
    : map;
};

export const predictionsReducer = (
  state: PredictionsState,
  action: WSPredictionResponse
): PredictionsState => {
  switch (action.type) {
    case 'SNAPSHOT': {
      const cache = new Map(
        action.data
          .filter(item => !state.dismissedPairIds.has(item.pairId))
          .map(item => [item.pairId, item])
      );
      return { cache, dismissedPairIds: state.dismissedPairIds };
    }
    case 'UPDATE': {
      const updated = new Map(state.cache);
      for (const opp of action.data) {
        if (!state.dismissedPairIds.has(opp.pairId)) {
          updated.set(opp.pairId, opp);
        }
      }
      return {
        cache: pruneExpired(updated),
        dismissedPairIds: state.dismissedPairIds
      };
    }
    case 'CLEANUP': {
      return {
        cache: pruneExpired(new Map(state.cache)),
        dismissedPairIds: state.dismissedPairIds
      };
    }
    case 'REMOVE_PAIR': {
      const updated = new Map(state.cache);
      updated.delete(action.pairId);
      const dismissed = new Set(state.dismissedPairIds);
      dismissed.add(action.pairId);
      return { cache: updated, dismissedPairIds: dismissed };
    }
    case 'CONFIRM_PAIR': {
      const existing = state.cache.get(action.pairId);
      if (!existing) return state;
      const updated = new Map(state.cache);
      updated.set(action.pairId, { ...existing, matchedBy: action.matchedBy });
      return { ...state, cache: updated };
    }
    case 'DELTA_UPDATE': {
      if (state.dismissedPairIds.has(action.opportunity.pairId)) return state;
      const updated = new Map(state.cache);
      updated.set(action.opportunity.pairId, action.opportunity);
      return { ...state, cache: updated };
    }
    case 'DELTA_REMOVE': {
      if (!state.cache.has(action.pairId)) return state;
      const updated = new Map(state.cache);
      updated.delete(action.pairId);
      return { ...state, cache: updated };
    }
    default:
      return state;
  }
};
